import React, { useRef } from 'react';

import { Card, CardContent, CardHeader, CardTitle, RadarChart } from '@mesh.ai/rabbit';
import { checkNewStaticDataFeaturesEnabled } from 'utils/helpers/featureFlags';

import noInsights from 'assets/assetsV1/illustrations/deerIllustrations/noInsights.svg';

import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';

import { CompetencySpiderChart } from '../../types/FeedbackMyOverview';

export const FeedbackSpiderChart = ({
  iSpiderChartLoading,
  spiderChartData,
}: {
  iSpiderChartLoading: boolean;
  spiderChartData: CompetencySpiderChart;
}) => {
  const ref = useRef();
  // @Nimmi: this is to be moved inside the dataHooks.ts file
  // Pass the data through a transformer function to get the data in the format required by the SpiderChart component
  const graphNodes = spiderChartData?.competencyGraphData
    ?.filter((competency) => !(competency.nodeType === 'COMPANY' && !checkNewStaticDataFeaturesEnabled()))
    ?.map((competency) => ({
      id: competency.employeeId ? String(competency.employeeId) : 'COMPANY',
      name: competency.nodeType === 'COMPANY' ? 'Company average score' : 'Competency score',
    }));

  const graphSeries = spiderChartData?.competencyGraphData
    ?.filter((competency) => !(competency.nodeType === 'COMPANY' && !checkNewStaticDataFeaturesEnabled()))
    ?.map((item) => ({
      data: item.scores
        .map((score) => score.score) // Extracting scores
        .filter((score) => score !== 0), // Filtering non-zero scores
      id: item.employeeId ? String(item.employeeId) : 'COMPANY',
    }));

  // filter non-zero competencies
  const filteredCompetencies = spiderChartData?.competencyDetails?.filter((comp) => {
    const score = spiderChartData?.competencyGraphData[0]?.scores?.find((sc) => sc.competencyId === comp.competencyId);
    return score.score !== 0;
  });

  const maxRating = spiderChartData?.maxRating;

  // extract out the names of the competencies
  const competencyNames = filteredCompetencies?.map((value) => value.competencyName);

  // Check if any employee competency data has a non-zero value
  const checkIfEmpCompetencyHasValue = !!graphSeries?.find(
    (series) => series.id !== 'COMPANY' && series.data.some((value) => value !== 0)
  );

  if (iSpiderChartLoading) {
    return <SkeletonLoader />;
  }

  if (!checkIfEmpCompetencyHasValue) {
    return (
      <Card
        style={{
          minHeight: '329px',
          height: '100%',
        }}
      >
        <CardContent>
          <EmptyScreen
            illustrationSrc={noInsights}
            heading="Not enough data to show"
            isSmallTextSize
            description="Insights on your relative performance across the company would appear here over time and with check-ins"
            smallTextMargin="0px"
          />
        </CardContent>
      </Card>
    );
  }

  if (spiderChartData?.competencyDetails?.length >= 15) {
    return (
      <Card
        style={{
          minHeight: '329px',
          height: '100%',
        }}
      >
        <CardContent>
          <EmptyScreen
            illustrationSrc={noInsights}
            heading="Too many data points to show"
            isSmallTextSize
            description="We are unable to show this graph because you have more than 15 competencies"
            smallTextMargin="0px"
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card style={{ height: '100%' }}>
      <CardHeader>
        <CardTitle>Distribution</CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className="rabbit-ds"
          style={{
            height: '265px',
            width: '100%',
          }}
        >
          {/* @ts-expect-error props */}
          <RadarChart
            ref={ref}
            nodes={graphNodes}
            yAxisDomain={[0, maxRating ?? 100]}
            series={graphSeries}
            tooltipTitle="Competency score"
            xAxisLabel="Competencies"
            xAxisValues={competencyNames}
          />
        </div>
      </CardContent>
    </Card>
  );
};
